"use client"

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import axios from 'axios'
import { 
  ShieldCheck, Filter, Search, Download, RefreshCw, Eye, EyeOff, 
  ChevronDown, ChevronRight, MoreVertical, CheckCircle, XCircle, 
  AlertCircle, BarChart3, Grid3X3, List, X, ArrowRight, ArrowDown
} from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu.jsx'
import { VisuallyHidden } from '../ui/visually-hidden.jsx'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Performance optimized Team Permissions component
export function TeamPermissions() {
  const [matrixData, setMatrixData] = useState([])
  const [permissions, setPermissions] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedRoles, setSelectedRoles] = useState(new Set())
  const [selectedCategories, setSelectedCategories] = useState(new Set())
  const [showInheritance, setShowInheritance] = useState(true)
  const [showUsage, setShowUsage] = useState(false)
  const [viewMode, setViewMode] = useState('matrix') // matrix, comparison, list
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedModules, setExpandedModules] = useState(new Set(['admin', 'team', 'users']))
  const [error, setError] = useState('')
  const [stats, setStats] = useState({ totalRoles: 0, totalPermissions: 0, renderTime: 0 })
  const [compareMode, setCompareMode] = useState(false)
  const [comparisonData, setComparisonData] = useState(null)

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load roles
        const rolesResponse = await axios.get(`${API}/rbac/roles`, { withCredentials: true })
        const rolesData = rolesResponse.data.filter(role => role.key !== 'subscriber')
        setRoles(rolesData)
        
        // Auto-select first 6 roles for initial display
        const initialSelection = new Set(rolesData.slice(0, 6).map(role => role.id))
        setSelectedRoles(initialSelection)
        
      } catch (err) {
        setError('Failed to load initial data')
        console.error('Initial data loading error:', err)
      }
    }
    
    loadInitialData()
  }, [])

  // Load permission matrix
  const loadMatrix = useCallback(async () => {
    if (selectedRoles.size === 0) {
      setMatrixData([])
      setPermissions([])
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError('')
      
      const roleIdsParam = Array.from(selectedRoles).join(',')
      const categoriesParam = selectedCategories.size > 0 ? Array.from(selectedCategories).join(',') : ''
      
      const params = new URLSearchParams()
      params.append('role_ids', roleIdsParam)
      if (categoriesParam) params.append('categories', categoriesParam)
      params.append('include_usage', showUsage.toString())
      params.append('include_inheritance', showInheritance.toString())

      const { data } = await axios.get(`${API}/admin/team/permissions/matrix?${params}`, { 
        withCredentials: true 
      })
      
      setMatrixData(data.matrix || [])
      setPermissions(Object.values(data.permissions_by_module || {}).flat())
      setStats({
        totalRoles: data.total_roles || 0,
        totalPermissions: data.total_permissions || 0,
        renderTime: data.render_time_ms || 0
      })
      
    } catch (err) {
      setError('Failed to load permission matrix')
      console.error('Permission matrix error:', err)
    } finally {
      setLoading(false)
    }
  }, [selectedRoles, selectedCategories, showUsage, showInheritance])

  useEffect(() => {
    loadMatrix()
  }, [loadMatrix])

  // Filter permissions by search term
  const filteredPermissions = useMemo(() => {
    if (!searchTerm) return permissions
    return permissions.filter(permission =>
      permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      permission.module.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [permissions, searchTerm])

  // Group permissions by module
  const permissionsByModule = useMemo(() => {
    const groups = {}
    filteredPermissions.forEach(permission => {
      const module = permission.module
      if (!groups[module]) groups[module] = []
      groups[module].push(permission)
    })
    return groups
  }, [filteredPermissions])

  // Toggle role selection
  const toggleRoleSelection = (roleId) => {
    const newSelection = new Set(selectedRoles)
    if (newSelection.has(roleId)) {
      newSelection.delete(roleId)
    } else {
      newSelection.add(roleId)
    }
    setSelectedRoles(newSelection)
  }

  // Toggle category selection
  const toggleCategorySelection = (category) => {
    const newSelection = new Set(selectedCategories)
    if (newSelection.has(category)) {
      newSelection.delete(category)
    } else {
      newSelection.add(category)
    }
    setSelectedCategories(newSelection)
  }

  // Toggle module expansion
  const toggleModuleExpansion = (module) => {
    const newExpanded = new Set(expandedModules)
    if (newExpanded.has(module)) {
      newExpanded.delete(module)
    } else {
      newExpanded.add(module)
    }
    setExpandedModules(newExpanded)
  }

  // Handle role comparison
  const handleCompareRoles = async () => {
    if (selectedRoles.size < 2) {
      alert('Please select at least 2 roles to compare')
      return
    }

    try {
      setCompareMode(true)
      const roleIdsParam = Array.from(selectedRoles).join(',')
      const { data } = await axios.get(`${API}/admin/team/permissions/compare?role_ids=${roleIdsParam}`, { 
        withCredentials: true 
      })
      setComparisonData(data)
    } catch (err) {
      setError('Failed to load role comparison')
      console.error('Role comparison error:', err)
    }
  }

  // Handle export
  const handleExport = async (format = 'csv') => {
    try {
      const exportData = {
        format: format,
        role_ids: Array.from(selectedRoles),
        include_inheritance: showInheritance,
        include_usage: showUsage
      }

      const { data } = await axios.post(`${API}/admin/team/permissions/export`, exportData, { 
        withCredentials: true 
      })
      
      // In a real implementation, this would poll for completion
      alert(`Export ${data.export_id} initiated. Download will be available shortly.`)
      
      // Simulate download after a short delay
      setTimeout(() => {
        const downloadUrl = `${API}/admin/team/permissions/export/${data.export_id}/download`
        window.open(downloadUrl, '_blank')
      }, 2000)
      
    } catch (err) {
      alert(`Export failed: ${err.response?.data?.detail || err.message}`)
    }
  }

  const categories = ['admin', 'team', 'users', 'monetization', 'ai', 'support', 'settings']

  return (
    <div className="team-permissions mobile-full-width">
      {/* Header with stats and controls */}
      <div className="flex-col-mobile items-start justify-between mb-6 mobile-full-width">
        <div className="w-full mobile-full-width">
          <h2 className="text-2xl font-semibold text-primary mb-2">Permission Matrix</h2>
          <div className="flex-col-mobile gap-4 text-sm text-muted mobile-full-width">
            <div className="flex gap-4 flex-wrap mobile-full-width">
              <span><ShieldCheck size={16} className="inline mr-1" />{stats.totalRoles} Roles</span>
              <span><Grid3X3 size={16} className="inline mr-1" />{stats.totalPermissions} Permissions</span>
              <span><BarChart3 size={16} className="inline mr-1" />Render: {stats.renderTime}ms</span>
              {stats.renderTime > 0 && (
                <span className={stats.renderTime < 3000 ? 'text-green-600' : 'text-red-600'}>
                  {stats.renderTime < 3000 ? '✓ Performance Target Met' : '⚠ Slow Render'}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto mobile-full-width">
          <select
            className="input flex-1 sm:min-w-32"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            aria-label="View mode"
          >
            <option value="matrix">Matrix View</option>
            <option value="comparison">Comparison View</option>
            <option value="list">List View</option>
          </select>
          
          <button 
            onClick={loadMatrix} 
            className="btn secondary"
            disabled={loading}
            aria-label="Refresh permission matrix"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            <VisuallyHidden>
              {loading ? 'Refreshing permission matrix data' : 'Reload permission matrix from server'}
            </VisuallyHidden>
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex-col-mobile gap-4 mb-6 mobile-full-width">
        {/* Role Selection */}
        <div className="w-full mobile-full-width">
          <label className="block text-sm font-medium text-primary mb-2">
            Select Roles ({selectedRoles.size} selected)
          </label>
          <div className="flex flex-wrap gap-2">
            {roles.map((role) => (
              <button
                key={role.id}
                onClick={() => toggleRoleSelection(role.id)}
                className={`btn text-xs ${selectedRoles.has(role.id) ? 'primary' : 'secondary'}`}
              >
                {role.name}
              </button>
            ))}
          </div>
        </div>

        {/* Category Filters */}
        <div className="w-full mobile-full-width">
          <label className="block text-sm font-medium text-primary mb-2">
            Filter by Categories ({selectedCategories.size > 0 ? selectedCategories.size : 'All'})
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => toggleCategorySelection(category)}
                className={`btn text-xs ${selectedCategories.has(category) ? 'primary' : 'secondary'}`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Search and Options */}
        <div className="flex-col-mobile gap-3 w-full mobile-full-width">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" size={16} />
            <input
              type="text"
              placeholder="Search permissions..."
              className="input pl-10 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search permissions by name, key, or module"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowInheritance(!showInheritance)}
              className={`btn text-xs ${showInheritance ? 'primary' : 'secondary'}`}
            >
              {showInheritance ? <EyeOff size={14} /> : <Eye size={14} />}
              Inheritance
            </button>
            
            <button
              onClick={() => setShowUsage(!showUsage)}
              className={`btn text-xs ${showUsage ? 'primary' : 'secondary'}`}
            >
              <BarChart3 size={14} />
              Usage
            </button>
            
            <button
              onClick={handleCompareRoles}
              className="btn secondary text-xs"
              disabled={selectedRoles.size < 2}
            >
              <ArrowRight size={14} />
              Compare
            </button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="btn secondary text-xs">
                  <Download size={14} />
                  Export
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  <Download size={14} className="mr-2" />
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  <Download size={14} className="mr-2" />
                  Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Content based on view mode */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-pulse">Loading permission matrix...</div>
        </div>
      ) : compareMode && comparisonData ? (
        <ComparisonView 
          comparisonData={comparisonData} 
          onClose={() => setCompareMode(false)}
        />
      ) : viewMode === 'matrix' ? (
        <MatrixView 
          matrixData={matrixData}
          permissionsByModule={permissionsByModule}
          expandedModules={expandedModules}
          onToggleModule={toggleModuleExpansion}
          showInheritance={showInheritance}
          showUsage={showUsage}
        />
      ) : viewMode === 'list' ? (
        <ListView 
          matrixData={matrixData}
          permissions={filteredPermissions}
          showInheritance={showInheritance}
        />
      ) : (
        <div className="text-center py-8">
          <AlertCircle size={48} className="mx-auto text-muted mb-4" />
          <p className="text-muted">Select view mode to display permissions</p>
        </div>
      )}
    </div>
  )
}

// Matrix View Component
function MatrixView({ matrixData, permissionsByModule, expandedModules, onToggleModule, showInheritance, showUsage }) {
  if (matrixData.length === 0) {
    return (
      <div className="text-center py-8">
        <Grid3X3 size={48} className="mx-auto text-muted mb-4" />
        <p className="text-muted">Select roles to display permission matrix</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Matrix Header - Role Names */}
      <div className="overflow-x-auto">
        <div className="min-w-full">
          <div className="flex bg-muted-50 border border-muted-200 rounded-lg p-3">
            <div className="w-48 flex-shrink-0 font-medium text-primary">
              Permissions
            </div>
            <div className="flex gap-4 flex-1">
              {matrixData.map((roleData) => (
                <div key={roleData.role.id} className="min-w-32 text-center">
                  <div className="font-medium text-primary text-sm">
                    {roleData.role.name}
                  </div>
                  <div className="text-xs text-muted">
                    {roleData.direct_count}D / {roleData.effective_count}E
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Permission Matrix by Module */}
      <div className="space-y-3">
        {Object.entries(permissionsByModule).map(([module, modulePermissions]) => (
          <div key={module} className="border border-muted-200 rounded-lg">
            {/* Module Header */}
            <button
              onClick={() => onToggleModule(module)}
              className="w-full flex items-center justify-between p-3 hover:bg-muted-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                {expandedModules.has(module) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span className="font-medium text-primary">
                  {module.charAt(0).toUpperCase() + module.slice(1)} 
                  <span className="text-muted ml-2">({modulePermissions.length})</span>
                </span>
              </div>
            </button>

            {/* Module Permissions */}
            {expandedModules.has(module) && (
              <div className="border-t border-muted-200">
                {modulePermissions.map((permission) => (
                  <PermissionRow
                    key={permission.id}
                    permission={permission}
                    matrixData={matrixData}
                    showInheritance={showInheritance}
                    showUsage={showUsage}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Individual Permission Row Component
function PermissionRow({ permission, matrixData, showInheritance, showUsage }) {
  return (
    <div className="flex items-center p-3 hover:bg-muted-50 border-b border-muted-100 last:border-b-0">
      <div className="w-48 flex-shrink-0">
        <div className="font-medium text-primary text-sm">
          {permission.name}
        </div>
        <div className="text-xs text-muted font-mono">
          {permission.key}
        </div>
      </div>
      
      <div className="flex gap-4 flex-1">
        {matrixData.map((roleData) => {
          const rolePermission = roleData.permissions.find(p => p.permission_id === permission.id)
          const hasPermission = rolePermission?.has_effective || false
          const isDirect = rolePermission?.has_direct || false
          const inheritedFrom = rolePermission?.inherited_from
          
          return (
            <div key={roleData.role.id} className="min-w-32 text-center">
              <div className="flex items-center justify-center">
                {hasPermission ? (
                  <div className="flex items-center gap-1">
                    <CheckCircle 
                      size={16} 
                      className={isDirect ? 'text-green-600' : 'text-blue-600'} 
                      title={isDirect ? 'Direct permission' : `Inherited from ${inheritedFrom}`}
                    />
                    {showInheritance && !isDirect && inheritedFrom && (
                      <span className="text-xs text-blue-600">I</span>
                    )}
                  </div>
                ) : (
                  <XCircle size={16} className="text-muted" title="Not granted" />
                )}
              </div>
              {showUsage && rolePermission?.usage_30d !== undefined && (
                <div className="text-xs text-muted mt-1">
                  {rolePermission.usage_30d} uses
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Comparison View Component
function ComparisonView({ comparisonData, onClose }) {
  const differingPermissions = comparisonData.permission_comparison.filter(p => p.has_differences)
  
  return (
    <div className="space-y-4">
      {/* Comparison Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-primary">Role Comparison</h3>
          <p className="text-sm text-muted">
            Comparing {comparisonData.roles.length} roles • 
            {comparisonData.comparison_stats.similarity_percentage}% similarity • 
            {differingPermissions.length} differences
          </p>
        </div>
        <button onClick={onClose} className="btn secondary">
          <X size={16} />
          Close
        </button>
      </div>

      {/* Comparison Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-primary">
            {comparisonData.comparison_stats.total_permissions}
          </div>
          <div className="text-sm text-muted">Total Permissions</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {comparisonData.comparison_stats.similarity_percentage}%
          </div>
          <div className="text-sm text-muted">Similarity</div>
        </div>
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">
            {differingPermissions.length}
          </div>
          <div className="text-sm text-muted">Differences</div>
        </div>
      </div>

      {/* Roles Being Compared */}
      <div className="flex gap-4 mb-6">
        {comparisonData.roles.map((role) => (
          <div key={role.id} className="card p-3 flex-1">
            <h4 className="font-medium text-primary">{role.name}</h4>
            <p className="text-xs text-muted">Level {role.hierarchy_level}</p>
          </div>
        ))}
      </div>

      {/* Differences Only */}
      <div className="space-y-2">
        <h4 className="font-medium text-primary">Permission Differences</h4>
        {differingPermissions.length === 0 ? (
          <p className="text-muted italic">No permission differences found</p>
        ) : (
          differingPermissions.map((permissionComp) => (
            <div key={permissionComp.permission.id} className="card p-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-primary">
                    {permissionComp.permission.name}
                  </span>
                  <span className="text-xs text-muted ml-2">
                    {permissionComp.permission.key}
                  </span>
                </div>
                <div className="flex gap-2">
                  {permissionComp.role_grants.map((grant) => (
                    <span
                      key={grant.role_id}
                      className={`text-xs px-2 py-1 rounded ${
                        grant.has_permission 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {grant.role_name}: {grant.has_permission ? 'Yes' : 'No'}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// List View Component
function ListView({ matrixData, permissions, showInheritance }) {
  if (matrixData.length === 0) {
    return (
      <div className="text-center py-8">
        <List size={48} className="mx-auto text-muted mb-4" />
        <p className="text-muted">Select roles to display permission list</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {matrixData.map((roleData) => (
        <div key={roleData.role.id} className="card p-4">
          <h3 className="font-medium text-primary mb-3">
            {roleData.role.name} 
            <span className="text-muted ml-2">
              ({roleData.effective_count} permissions)
            </span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {roleData.permissions
              .filter(p => p.has_effective)
              .map((permission) => (
                <div key={permission.permission_id} className="flex items-center gap-2 text-sm">
                  <CheckCircle 
                    size={14} 
                    className={permission.has_direct ? 'text-green-600' : 'text-blue-600'} 
                  />
                  <span className="flex-1">
                    {permission.permission_name}
                  </span>
                  {showInheritance && !permission.has_direct && permission.inherited_from && (
                    <span className="text-xs text-blue-600">
                      ({permission.inherited_from})
                    </span>
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default TeamPermissions